
import { supabase } from "@/integrations/supabase/client";
import { validateGroupConsistency } from "@/utils/productGroups";
import { toast } from "sonner";

/**
 * Run a consistency check on all product descriptions in a collection
 * to ensure variants within groups have the same descriptions
 */
export const runConsistencyCheck = async (collectionId: string): Promise<{
  fixed: number;
  groups: number;
}> => {
  try {
    console.log(`Running consistency check for collection ${collectionId}`);
    
    const result = await validateGroupConsistency(collectionId);
    
    if (result.groups > 0) {
      toast.success(`Fixed inconsistent descriptions in ${result.groups} product groups`);
    } else {
      toast.success("All product descriptions are consistent!");
    }
    
    return result;
  } catch (error) {
    console.error("Error running consistency check:", error);
    toast.error("Failed to complete consistency check");
    return { fixed: 0, groups: 0 };
  }
};

/**
 * Get statistics about inconsistent descriptions within product groups
 */
export const getGroupConsistencyStats = async (collectionId: string): Promise<{
  totalGroups: number;
  inconsistentGroups: number;
  affectedProducts: number;
}> => {
  try {
    // Use a transaction to ensure we get a consistent snapshot of the database
    const { data, error } = await supabase.rpc('get_group_consistency_stats', {
      p_collection_id: collectionId
    });

    if (error) {
      console.error("Error getting consistency stats:", error);
      // Fallback to client-side calculation if the RPC fails
      return getClientSideConsistencyStats(collectionId);
    }
    
    return data || { totalGroups: 0, inconsistentGroups: 0, affectedProducts: 0 };
  } catch (error) {
    console.error("Error getting consistency stats:", error);
    return { totalGroups: 0, inconsistentGroups: 0, affectedProducts: 0 };
  }
};

/**
 * Fallback to calculate consistency stats client-side if the RPC fails
 */
const getClientSideConsistencyStats = async (collectionId: string): Promise<{
  totalGroups: number;
  inconsistentGroups: number;
  affectedProducts: number;
}> => {
  try {
    // Get all content groups in this collection
    const { data: productGroups, error: groupsError } = await supabase
      .from("product_descriptions")
      .select("content_id")
      .eq("collection_id", collectionId)
      .not("content_id", "is", null);
    
    if (groupsError) throw groupsError;
    
    if (!productGroups || productGroups.length === 0) {
      return { totalGroups: 0, inconsistentGroups: 0, affectedProducts: 0 };
    }
    
    // Extract unique content_ids 
    const uniqueContentIds = Array.from(new Set(productGroups.map(item => item.content_id)));
    
    let inconsistentGroups = 0;
    let affectedProducts = 0;
    
    // For each unique content_id, check for inconsistencies
    for (const contentId of uniqueContentIds) {
      if (!contentId) continue;
      
      // Get all products in this group
      const { data: groupProducts, error: productsError } = await supabase
        .from("product_descriptions")
        .select("id, improved_description, generated_description")
        .eq("content_id", contentId)
        .eq("collection_id", collectionId);
      
      if (productsError || !groupProducts || groupProducts.length <= 1) continue;
      
      // Find descriptions
      const descriptions = groupProducts.map(p => p.improved_description || p.generated_description);
      
      // Check if all descriptions are the same
      const uniqueDescriptions = new Set(descriptions.filter(d => d && d.trim().length > 0));
      if (uniqueDescriptions.size > 1) {
        // Found inconsistency
        inconsistentGroups++;
        affectedProducts += groupProducts.length;
      }
    }
    
    return {
      totalGroups: uniqueContentIds.length,
      inconsistentGroups,
      affectedProducts
    };
  } catch (error) {
    console.error("Error getting client-side consistency stats:", error);
    return { totalGroups: 0, inconsistentGroups: 0, affectedProducts: 0 };
  }
};

/**
 * Force synchronize descriptions within groups with transaction guarantees
 */
export const forceGroupConsistency = async (collectionId: string): Promise<{
  fixed: number;
  groups: number;
}> => {
  try {
    // Use the database function that enforces consistency with transactions
    const { data, error } = await supabase.rpc('validate_collection_consistency', {
      p_collection_id: collectionId
    });
    
    if (error) {
      console.error("Error forcing group consistency:", error);
      throw error;
    }
    
    return {
      fixed: data.fixed_count || 0,
      groups: data.fixed_groups || 0
    };
  } catch (error) {
    console.error("Error forcing group consistency:", error);
    // Fall back to the non-transactional method
    return validateGroupConsistency(collectionId);
  }
}
